import React from 'react';
import I18n from 'i18n-js';
import {
  core_apimessages_Money as Money,
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
} from '../../src/schemas/rql';
import { useUser, useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import { inMobileBrowser } from '@reverbdotcom/commons/src/user_context_helpers';
import CondensedOrderSummary from './condensed_order_summary';
import { useApplePayAvailabilityByConfig } from './useApplePayAvailability';
import experiments from '@reverbdotcom/commons/src/experiments';
import { CheckoutErrorAlertBox } from './checkout/CheckoutErrorAlertBox';
import CheckoutPayPalButton, { isCartEligible } from './CheckoutPayPalButton';
import classNames from 'classnames';
import URLHelpers from './shared/url_helpers';
import { PAYPAL_PRESENTMENT_CURRENCIES } from '@reverbdotcom/commons/src/currency';
import { ExpressPaySeparator } from './ExpressPaySeparator';
import { CartBundleExpressApplePayButton } from './CartBundleExpressApplePayButton';

interface Props {
  paymentMethodType: string;
  checkoutBundlingId: string;
  applePayCheckoutBundlingId: string;
  amountTotal: Money
  adyenCheckoutPaymentMethodsConfig: string;
  currency: string;
  paypalCheckoutExpAttributes: {
    currencyCount: number,
    bundleCount: number,
    shopCount: number,
    isLocalPickupOnly: boolean,
    includesStandalonePaypal: boolean,
  },
  showReverbProtectionCallout: boolean,
}

export default function CartHoistedExpressPayWrapper(props: Props) {
  const user = useUser();
  const [errors, setErrors] = React.useState([]);
  const paypalCurrency = usePaypalCurrency({ fallbackCurrency: props.currency });

  const [isApplePayAvailable] = useApplePayAvailabilityByConfig(props.adyenCheckoutPaymentMethodsConfig);
  const showApplePayButton = isApplePayAvailable && props.paymentMethodType == CheckoutPaymentMethodType.DIRECT_CHECKOUT;

  const isCartEligibleForPaypalExp = isCartEligible(props.paypalCheckoutExpAttributes);
  const inPaypalButtonRollout = useExpEnabled(experiments.PAYPAL_CHECKOUT_BUTTON);
  const showPaypalButton = inPaypalButtonRollout && isCartEligibleForPaypalExp;

  const showExpressPay = inPaypalButtonRollout
    && (showApplePayButton || showPaypalButton);

  const classes = classNames(
    'checkout-cart-header',
    { 'checkout-cart-header--express': inPaypalButtonRollout },
  );

  if (!showExpressPay && !showApplePayButton) {
    return null;
  }

  return (
    <div className={classes}>
      {inMobileBrowser(user) &&
        <CondensedOrderSummary
          itemPlusShippingSubtotal={props.amountTotal.display}
        />
      }
      {showExpressPay && props.showReverbProtectionCallout &&
        <div className="mtb-3">
          <p className="cart-payment-methods__protection">
            {I18n.t('discovery.paymentMethodSelection.protection.buyWithConfidence')}
            <span className="ml-space">
              <a
                href={URLHelpers.reverbProtectionPath}
                target="_blank"
                rel="noreferrer"
                className="text-link"
              >
                {I18n.t('discovery.paymentMethodSelection.protection.protectionIncluded')}
              </a>
            </span>
          </p>
        </div>
      }
      {showExpressPay &&
        <>
          {errors.length > 0 &&
            <div className="mb-2">
              <CheckoutErrorAlertBox errors={errors} />
            </div>
          }
          <ExpressPayDeviceLayout
            paypalButton={showPaypalButton &&
              <CheckoutPayPalButton
                currency={paypalCurrency}
                checkoutBundlingId={props.checkoutBundlingId}
                onError={setErrors}
                variant="cart"
                parentComponentName="CartHoistedExpressPayWrapper"
                listingCurrency={props.currency}
              />
            }
            applePayButton={showApplePayButton &&
              <CartBundleExpressApplePayButton
                variant="black-express"
                checkoutBundlingId={props.applePayCheckoutBundlingId}
                amountTotal={props.amountTotal}
                adyenCheckoutPaymentMethodsConfig={props.adyenCheckoutPaymentMethodsConfig}
                tracking={{
                  displayStyle: 'hoisted',
                }}
              />
            }
          />
        </>
      }
      {inMobileBrowser(user) && !showExpressPay && showApplePayButton &&
        <div className="checkout-cart-header__button">
          <div className="checkout-cart-header__button--apple-pay">
            <CartBundleExpressApplePayButton
              variant="black"
              checkoutBundlingId={props.checkoutBundlingId}
              amountTotal={props.amountTotal}
              adyenCheckoutPaymentMethodsConfig={props.adyenCheckoutPaymentMethodsConfig}
              tracking={{
                displayStyle: 'hoisted',
              }}
            />
          </div>
        </div>
      }
    </div>
  );
}

function ExpressPayDeviceLayout({
  paypalButton,
  applePayButton,
}: {
  paypalButton: React.ReactNode,
  applePayButton: React.ReactNode,
}) {
  const user = useUser();

  if (inMobileBrowser(user)) {
    return (
      <>
        <h2 className="weight-bold size-140 mb-2">
          {I18n.t('discovery.checkout.expressCheckout.title')}
        </h2>
        <div className="mb-1">
          {!!paypalButton &&
            paypalButton
          }
        </div>
        {!!applePayButton &&
          applePayButton
        }
        <div className="mt-2 mb-1">
          <ExpressPaySeparator />
        </div>
      </>
    );
  } else {
    return (
      <div className="d-flex fx-dir-col fx-align-center">
        <h2 className="weight-bold size-140 mb-2">
          {I18n.t('discovery.checkout.expressCheckout.title')}
        </h2>
        <div className="d-flex fx-dir-col fx-gap-1 width-rem-50">
          {!!paypalButton &&
            paypalButton
          }
          {!!applePayButton &&
            applePayButton
          }
        </div>
        <div className="mt-2 mb-1 width-100">
          <ExpressPaySeparator condensed />
        </div>
      </div>
    );
  }
}

function usePaypalCurrency({ fallbackCurrency }: { fallbackCurrency: string }) {
  const user = useUser();

  const inPayPalPresentmentCurrencyRollout = useExpEnabled(experiments.DC_PAYPAL_ALL_THE_PRESENTMENT_CURRENCIES);
  const isSupportedCurrency = PAYPAL_PRESENTMENT_CURRENCIES.includes(user.currency);

  if (inPayPalPresentmentCurrencyRollout && isSupportedCurrency) {
    return user.currency;
  }

  return fallbackCurrency;
}
